import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  DateRangePickerProps,
  TableProps,
} from '@cloudscape-design/components';

import { useNavigate } from '@tanstack/react-router';
import { getPlaceWebsiteContactMeta, getPlaceWebsiteContacts } from '../common/api/places';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../common/filtering/date-filtering';
import { ResourceError } from './Loading';
import { exportExcel } from '../common/excel';
import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { WebsiteContact } from '../common/types';
import { formatDateString } from '../common/formatting/dateFormat';
import { PhoneNumber } from './PhoneNumber';
import { Email } from './Email';
import { formatName } from '../common/names';

export type WebsiteContactTableProps = {
  uuid: string;
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideActions?: boolean;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  additionalActions?: React.ReactNode;
  selectedItems?: WebsiteContact[];
  setSelectedItems?: (items: WebsiteContact[]) => void;
};

export function WebsiteContactTable(props: WebsiteContactTableProps) {
  const {
    uuid,
    now,
    setNow,
    query,
    setQuery,
    range,
    setRange,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
    selectedItems,
    setSelectedItems,
  } = props;
  const navigate = useNavigate();
  const resourceName = 'Website Contacts';
  const endpointName = 'website-contacts';
  // relative times are in the past vs future
  const past = true;

  // TODO move to context
  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // const queryClient = useQueryClient();
  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: () => getPlaceWebsiteContactMeta(uuid),
    staleTime: 360_000, // 10 minutes
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions.map(o => ({ ...o, label: o.label ?? undefined })),
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [endpointName, query, range, pageSize, sortingField, sortDirection, page, now],
    enabled: metaQuery.isSuccess,
    // TODO add sorting
    queryFn: async () =>
      await getPlaceWebsiteContacts(
        uuid,
        query,
        page,
        pageSize,
        range,
        sortingField,
        sortDirection,
        now,
      ),
    staleTime: 60_000,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <Table
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{
        sortingField,
      }}
      // default to ascending
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          description="View discovered contacts."
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                {props.additionalActions}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                <Button
                  disabled={loading}
                  iconName="download"
                  onClick={() => {
                    exportExcel(
                      (data?.items ?? []).map((item) => ({
                        first_name: item.first_name,
                        last_name: item.last_name,
                        phone_number: item.phone_number,
                        email: item.email,
                        title: item.title,
                        department: item.department,
                        updated_at: item.updated_at,
                      })),
                      'contacts',
                    );
                  }}
                />
              </SpaceBetween>
            )
          }
          counter={
            data?.total && metaQuery.data?.total
              ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      items={data?.items ?? []}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{
            options: PAGE_SIZE_OPTIONS,
          }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <PropertyDateFilter
            loading={loading}
            query={query}
            setQuery={(q) => {
              setQuery?.(q);
              // reset page when filtering
              setPage(1);
            }}
            range={range ?? null}
            setRange={(r) => {
              setRange?.(r);
              // reset page when filtering
              setPage(1);
            }}
            propertyFilteringOptions={options}
            filteringProperties={properties}
            past={past}
          />
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        // {
        //   id: 'name',
        //   header: 'Name',
        //   cell: (item: WebsiteContact) => (
        //     <Link
        //       href={`/contacts/${item.uuid}`}
        //       variant="secondary"
        //       target="_blank"
        //       // onFollow={(e) => {
        //       //   e.preventDefault();
        //       //   navigate(`/contacts/${item.uuid}`);
        //       // }}
        //     >
        //       {item.first_name} {item.last_name}
        //     </Link>
        //   ),
        //   sortingField: 'first_name',
        // },
        // {
        //   id: 'place',
        //   header: 'Place',
        //   cell: (item: WebsiteContact) => (
        //     <Link
        //       href={`/places/${item.place.uuid}`}
        //       variant="secondary"
        //       target="_blank"
        //       // onFollow={(e) => {
        //       //   e.preventDefault();
        //       //   navigate(`/places/${item.place.uuid}`);
        //       // }}
        //     >
        //       {item.place.name} ({item.place.rating})
        //     </Link>
        //   ),
        // },
        {
          id: 'name',
          header: 'Name',
          cell: (item: WebsiteContact) => formatName(item.first_name, item.last_name),
          sortingField: 'first_name',
        },
        {
          id: 'phone',
          header: 'Phone',
          cell: (item: WebsiteContact) => (
            <PhoneNumber
              phoneNumber={item.phone_number}
              lineTypeInfo={item.phone_line_type_info}
              showLineTypeInfo
            />
          ),
          sortingField: 'phone_number',
        },
        {
          id: 'email',
          header: 'Email',
          cell: (item: WebsiteContact) => <Email email={item.email} />,
          sortingField: 'email',
        },
        {
          id: 'title',
          header: 'Title',
          cell: (item: WebsiteContact) => `${item.title ?? '-'}`,
          sortingField: 'title',
        },
        {
          id: 'department',
          header: 'Department',
          cell: (item: WebsiteContact) => `${item.department ?? '-'}`,
          sortingField: 'department',
        },
        {
          id: 'updated_at',
          header: 'Updated',
          cell: (item: WebsiteContact) => formatDateString(item.updated_at),
        },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
  );
}
